import memoizeOne from 'memoize-one';
import { AnalyticDataSource, AnalyticDataSources } from '~/Api/Analytics/VirtualBrand/mapAnalyticVirtualBrand';
import { Country } from '~/Api/Country';
import { DataSource } from '~/Api/DataSource';

import ActionTypes from './ActionTypes';

export interface State {
	request: Promise<any> | null;
	isFetching: boolean;
	dataSources: DataSource[] | null;
}

type Action =
	| {
			type: ActionTypes.request;
			request: Promise<any>;
	  }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			dataSources: DataSource[] | null;
	  };

function createState(): State {
	return {
		request: null,
		isFetching: false,
		dataSources: null,
	};
}

export default function reducer(state: State = createState(), action: Action) {
	switch (action.type) {
		case ActionTypes.request:
			return {
				...state,
				request: action.request,
				isFetching: true,
				dataSources: null,
			};

		case ActionTypes.receive:
			if (state.request !== action.request) {
				return state;
			}

			return {
				...state,
				request: action.dataSources ? action.request : null,
				isFetching: false,
				dataSources: action.dataSources,
			};

		default:
			return state;
	}
}

export const selectDataSourcesByCountry = memoizeOne(
	(dataSources: DataSource[]): { [key: string]: DataSource[] } | null => {
		return (
			dataSources.reduce((result: { [key: string]: DataSource[] }, dataSource: DataSource) => {
				if (result[dataSource.country.alpha2]) {
					result[dataSource.country.alpha2].push(dataSource);
				} else {
					result[dataSource.country.alpha2] = [dataSource];
				}
				return result;
			}, {}) || null
		);
	}
);

export const selectAnalyticalDataSourcesByCountry = memoizeOne(
	(analyticalDataSrouces: AnalyticDataSources): { [key: string]: (DataSource | null)[] } => {
		return analyticalDataSrouces.reduce(
			(result: { [key: string]: (DataSource | null)[] }, { dataSource, country }: AnalyticDataSource) => {
				if (result[country.alpha2]) {
					result[country.alpha2].push(dataSource);
				} else {
					result[country.alpha2] = [dataSource];
				}
				return result;
			},
			{}
		);
	}
);

export function selectDataSourceCountries(dataSources: DataSource[]): Country[] {
	const countryCodes: Set<string> = new Set();
	const countries = dataSources.reduce((result: Country[], dataSource: DataSource) => {
		if (!countryCodes.has(dataSource.country.alpha2)) {
			countryCodes.add(dataSource.country.alpha2);
			result.push(dataSource.country);
		}
		return result;
	}, []);

	return countries.sort((country, otherCountry) => country.alpha2.localeCompare(otherCountry.alpha2));
}

export function selecAnalyticaltDataSourceCountries(dataSources: Pick<AnalyticDataSource, 'country'>[]): Country[] {
	const countryCodes: Set<string> = new Set();
	const countries = dataSources.reduce((result: Country[], { country }: Pick<AnalyticDataSource, 'country'>) => {
		if (!countryCodes.has(country.alpha2)) {
			countryCodes.add(country.alpha2);
			result.push(country);
		}
		return result;
	}, []);

	return countries.sort((country, otherCountry) => country.alpha2.localeCompare(otherCountry.alpha2));
}
